/* Custom query string builder
Usage example:

Input:

// Object-based structure
const sampleInput = {
  status: [
    {
      operator: "eq",
      value: "active"
    }
  ]
}

// Array-based structure
const arrayInput = [
  {
    column: "requisition_status",
    operator: "eq",
    value: "ACCOUNTANT"
  }
]

const queryBuilder = new QueryBuilder();
const resultQuery = queryBuilder.filter(sampleInput);

Output:

console.log(resultQuery);
"?filters[0][column]=status&filters[0][operator]=eq&filters[0][value]=active"
or
"?filters[0][column]=requisition_status&filters[0][operator]=eq&filters[0][value]=ACCOUNTANT"

*/
// Filter values come from user input and enum option lists, so they can contain
// characters that are meaningful in a query string ("&", "=", "#"). Columns and
// operators are internal identifiers and stay as-is for readable URLs.
const encodeValue = value => encodeURIComponent(value);

export class QueryBuilder {
  constructor() {
    this.queryString = "";
    this.filterIndex = 0;
  }

  filter(filterObject) {
    // Handle array-based structure
    if (Array.isArray(filterObject)) {
      filterObject.forEach(filter => {
        if (Array.isArray(filter.value)) {
          let multipleValuesString = `&filters[${this.filterIndex}][column]=${filter.column}&filters[${this.filterIndex}][operator]=${filter.operator}`;
          for (const [valueIndex, value] of filter.value.entries()) {
            multipleValuesString += `&filters[${
              this.filterIndex
            }][value][${valueIndex}]=${encodeValue(value)}`;
          }
          this.queryString += multipleValuesString;
          this.filterIndex++;
        } else {
          let filterString = `&filters[${this.filterIndex}][column]=${filter.column}&filters[${this.filterIndex}][operator]=${filter.operator}`;
          if (filter.value !== undefined) {
            filterString += `&filters[${this.filterIndex}][value]=${encodeValue(
              filter.value
            )}`;
          }
          this.queryString += filterString;
          this.filterIndex++;
        }
      });
      return;
    }

    // Handle object-based structure
    for (const [filterKey, filterValues] of Object.entries(filterObject)) {
      for (const filterValue of filterValues) {
        if (Array.isArray(filterValue.value)) {
          let multipleValuesString = `&filters[${this.filterIndex}][column]=${filterKey}&filters[${this.filterIndex}][operator]=${filterValue.operator}`;
          for (const [valueIndex, value] of filterValue.value.entries()) {
            multipleValuesString += `&filters[${
              this.filterIndex
            }][value][${valueIndex}]=${encodeValue(value)}`;
          }
          this.queryString += multipleValuesString;
          this.filterIndex++;
        } else {
          let filterString = `&filters[${this.filterIndex}][column]=${filterKey}&filters[${this.filterIndex}][operator]=${filterValue.operator}`;
          if (filterValue.value !== undefined) {
            filterString += `&filters[${this.filterIndex}][value]=${encodeValue(
              filterValue.value
            )}`;
          }
          this.queryString += filterString;
          this.filterIndex++;
        }
      }
    }
  }

  search(searchValue) {
    if (searchValue !== "") {
      this.queryString += `&search[value]=${searchValue}`;
    }
  }

  sort(sortObject) {
    for (const [sortKey, sortValue] of Object.entries(sortObject)) {
      this.queryString += `&sort[${sortKey}]=${sortValue}`;
    }
  }

  groupBy(groupByArray) {
    for (const groupByValue of groupByArray) {
      this.queryString += `&group_by[]=${groupByValue}`;
    }
  }

  pagination(page, pageSize) {
    this.queryString += `&page=${page}`;
    this.queryString += `&per_page=${pageSize}`;
  }

  startDate(startDate) {
    this.queryString += `&start_date=${startDate}`;
  }

  endDate(endDate) {
    this.queryString += `&end_date=${endDate}`;
  }

  append(key, value) {
    this.queryString += `&${key}=${value}`;
  }

  reset() {
    this.queryString = "";
    this.filterIndex = 0;
  }

  build() {
    return `?${this.queryString.slice(1)}`;
  }
}
